import type { OverviewSubject } from 'src/clients/hooks/useOverview';
import type { ParticipantFamily } from 'src/meetings/utils/event-utils';

export type MeetingOverview = {
  key: string;
  subject: OverviewSubject;
  name: string;
};

type Attendee = {
  id: string;
  name: string;
};

// The single family every client attendee shares, or undefined when they span
// different families or any attendee has no family. A lone attendee who is part
// of a family counts as sharing it.
function sharedFamilyOf(
  attendees: Attendee[],
  familiesByClientId: Map<string, ParticipantFamily>,
): ParticipantFamily | undefined {
  const families = attendees
    .map(attendee => familiesByClientId.get(attendee.id))
    .filter((family): family is ParticipantFamily => !!family);
  if (families.length === 0 || families.length !== attendees.length) return undefined;
  const distinctFamilyIds = new Set(families.map(family => family.id));
  return distinctFamilyIds.size === 1 ? families[0] : undefined;
}

// One family overview when every client attendee belongs to the same family
// (the family route always resolves to its own family); otherwise one individual
// overview per attendee, so unrelated clients are never grouped under a family.
export function resolveMeetingOverviews(
  attendees: Attendee[],
  familiesByClientId: Map<string, ParticipantFamily>,
  routeFamily: ParticipantFamily | null,
): MeetingOverview[] {
  const sharedFamily = routeFamily ?? sharedFamilyOf(attendees, familiesByClientId);
  if (sharedFamily) {
    return [
      {
        key: sharedFamily.id,
        subject: { kind: 'family', familyId: sharedFamily.id },
        name: sharedFamily.name,
      },
    ];
  }
  return attendees.map(attendee => ({
    key: attendee.id,
    subject: { kind: 'client', clientId: attendee.id },
    name: attendee.name,
  }));
}
